import { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { aiAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { getCurrentUser } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { trackUserAction, trackChatEvent, trackSafetyEvent, trackMiniAppEvent } from '../../utils/analytics';
import { searchPreflight, SEARCH_SKIP_AUTH_AND_LICENSE, getPreflightFailureKind, SEARCH_PREFLIGHT_SERVICE_ERROR_MESSAGE } from '../../utils/searchPreflight';
import { notifySearchLicenseRequired } from '../../utils/searchLicenseNotify';
import { GOOGLE_CSE_IDS, supportedFileTypes } from './SearchPage.constants';
import { devLog } from '../../utils/devLog';

const CSE_RESULTS_ROOT_ID = 'web-search-results-root';
const CSE_GNAME = 'exploreWebSearch';

function runWhenDocumentReady(fn) {
  if (document.readyState === 'complete') {
    fn();
    return;
  }
  if (window.google?.setOnLoadCallback) {
    window.google.setOnLoadCallback(fn, true);
    return;
  }
  window.addEventListener('load', fn, { once: true });
}

function removeCseScripts() {
  document.querySelectorAll('script[src*="cse.google.com"]').forEach((script) => script.remove());
}

let cseScriptCx = null;

function loadCseScript(cseId) {
  if (cseScriptCx === cseId && document.querySelector('script[src*="cse.google.com"]')) {
    return null;
  }
  removeCseScripts();
  cseScriptCx = cseId;
  const script = document.createElement('script');
  script.src = `https://cse.google.com/cse.js?cx=${cseId}`;
  script.async = true;
  document.head.appendChild(script);
  return script;
}

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

/**
 * When false: hides AI overview / “thinking” UI on the search page and skips overview API calls.
 * Set to true to restore.
 */
export const ENABLE_AI_SEARCH_OVERVIEW = false;

export const useSearchPage = (searchType) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth();
  const query = useQuery().get('q') || '';
  const typeParam = useQuery().get('type') || 'web';
  
  // State management
  const [aiOverview, setAiOverview] = useState(null);
  const [currentSearchType, setCurrentSearchType] = useState(typeParam);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResponseType, setAiResponseType] = useState(null);
  const [showAiOverview, setShowAiOverview] = useState(true);
  const [gcsLoaded, setGcsLoaded] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showSuggestionBar, setShowSuggestionBar] = useState(false);
  const [showSuggestionsList, setShowSuggestionsList] = useState(false);
  const [showAppLauncher, setShowAppLauncher] = useState(false);
  const [isChatFocused, setIsChatFocused] = useState(false);
  const [searchInput, setSearchInput] = useState(query);
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const [blockedMessage, setBlockedMessage] = useState(null);
  const [blockedReason, setBlockedReason] = useState(null);
  const [searchGateReady, setSearchGateReady] = useState(false);
  const [aiFullScreen, setAiFullScreen] = useState(false);
  const [messageInputValue, setMessageInputValue] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);

  // Refs
  const searchResultsRef = useRef(null);
  const searchBoxRef = useRef(null);
  const aiOverviewRequested = useRef(false);
  const cseCxRef = useRef(null);
  const cseReadyRef = useRef(false);
  const messageInputRef = useRef(null);
  const fileUploadRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const lastMessageRef = useRef(null);
  const lastQuery = useRef('');
  const mainContainerRef = useRef(null);
  const profileRef = useRef(null);

  // Cache for AI Overview responses
  const aiOverviewCache = useRef(new Map());
  
  // Load cache from localStorage on component mount
  useEffect(() => {
    try {
      const cachedData = localStorage.getItem('aiOverviewCache');
      if (cachedData) {
        const parsedCache = JSON.parse(cachedData);
        const now = Date.now();
        const oneDay = 24 * 60 * 60 * 1000;
        const validEntries = Object.entries(parsedCache).filter(([key, value]) => {
          return (now - value.timestamp) < oneDay;
        });
        aiOverviewCache.current = new Map(validEntries);
        devLog('Loaded AI overview cache from localStorage:', aiOverviewCache.current.size, 'entries');
      }
    } catch (error) {
      console.error('Error loading AI overview cache:', error);
    }
  }, []);
  
  // Save cache to localStorage whenever it changes
  const saveCacheToStorage = useCallback(() => {
    try {
      const cacheObject = Object.fromEntries(aiOverviewCache.current);
      localStorage.setItem('aiOverviewCache', JSON.stringify(cacheObject));
    } catch (error) {
      console.error('Error saving AI overview cache:', error);
    }
  }, []);

  // Helper function to get user ID
  const getUserId = () => {
    const user = getCurrentUser();
    if (user && user.uid) {
      return user.uid;
    }
    return 'guest_' + Math.random().toString(36).substring(2, 15);
  };

  // Update currentSearchType when URL parameters change
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const newTypeParam = urlParams.get('type') || 'web';
    devLog('URL type param changed:', newTypeParam, 'Current location:', location.search);
    if (newTypeParam && GOOGLE_CSE_IDS[newTypeParam]) {
      setCurrentSearchType(newTypeParam);
    } else {
      setCurrentSearchType('web');
    }
  }, [location.search]);

  // Keyword gate (+ license/auth when SEARCH_SKIP_AUTH_AND_LICENSE is false)
  useEffect(() => {
    if (!SEARCH_SKIP_AUTH_AND_LICENSE && authLoading) {
      return;
    }

    if (!query || query.trim() === '') {
      setSearchGateReady(true);
      setBlockedMessage(null);
      setBlockedReason(null);
      return;
    }

    let cancelled = false;
    setSearchGateReady(false);

    const checkInitialQuery = async () => {
      const user = getCurrentUser();
      const firebaseId = user ? user.uid : null;

      try {
        const preflight = await searchPreflight(query);
        if (cancelled) return;

        if (preflight.reason === 'auth_required') {
          setBlockedReason('auth_required');
          setBlockedMessage(null);
          return;
        }
        if (preflight.reason === 'license_required') {
          setBlockedReason('license_required');
          setBlockedMessage(null);
          return;
        }
        const failureKind = getPreflightFailureKind(preflight);
        if (failureKind === 'service_error') {
          setBlockedReason('service_error');
          setBlockedMessage(null);
          return;
        }
        if (failureKind === 'keyword_blocked') {
          setBlockedReason('keyword_blocked');
          setBlockedMessage(preflight.search_keyword);
          trackSafetyEvent.keywordBlocked(query, firebaseId);
          return;
        }

        setBlockedMessage(null);
        setBlockedReason(null);
        setSearchInput(query);
        devLog('Search preflight passed for URL query:', query);
      } catch (error) {
        if (!cancelled) {
          console.error('Initial keyword check failed:', error);
          setBlockedMessage(null);
        }
      } finally {
        if (!cancelled) {
          setSearchGateReady(true);
        }
      }
    };

    checkInitialQuery();

    return () => {
      cancelled = true;
    };
  }, [query, authLoading]);

  // Memoized function to fetch AI overview
  const fetchAIOverview = useCallback(async (searchQuery) => {
    if (!ENABLE_AI_SEARCH_OVERVIEW) return;
    if (!searchQuery || aiOverviewRequested.current) return;
    
    const cacheKey = searchQuery.toLowerCase().trim();
    const cachedResponse = aiOverviewCache.current.get(cacheKey);
    
    if (cachedResponse) {
      devLog('Using cached AI overview for query:', searchQuery);
      if (cachedResponse.data && cachedResponse.data.confidence >= 0.85) {
        setAiResponseType('direct_response');
        setAiOverview(cachedResponse.data.response);
        setShowAiOverview(true);
      } else {
        setAiResponseType(null);
        setAiOverview(null);
        setShowAiOverview(false);
      }
      setAiLoading(false);
      return;
    }
    
    aiOverviewRequested.current = true;
    setAiLoading(true);
    setAiResponseType(null);
    
    try {
      const data = await aiAPI.fetchOverview(searchQuery);
      if (data && !data.error) {
        aiOverviewCache.current.set(cacheKey, {
          data: data,
          timestamp: Date.now()
        });
        if (aiOverviewCache.current.size > 50) {
          const firstKey = aiOverviewCache.current.keys().next().value;
          aiOverviewCache.current.delete(firstKey);
        }
        saveCacheToStorage();
      }
      
      if (data && data.confidence >= 0.85) {
        setAiResponseType('direct_response');
        setAiOverview(data.response);
        setShowAiOverview(true);
        setTimeout(() => {
          // Only scroll on desktop to prevent interference with mobile chat scrolling
          if (window.innerWidth > 768) {
            scrollToLatestMessage();
          }
        }, 200);
      } else {
        setAiResponseType(null);
        setAiOverview(null);
        setShowAiOverview(false);
      }
    } catch (err) {
      console.error('AI Overview error:', err);
      setShowAiOverview(false);
      setAiResponseType(null);
    } finally {
      setAiLoading(false);
    }
  }, [saveCacheToStorage]);

  // Fetch AI Overview - only once per query
  useEffect(() => {
    if (!ENABLE_AI_SEARCH_OVERVIEW) {
      if (query) lastQuery.current = query;
      return;
    }
    devLog('AI Overview useEffect triggered:', {
      query,
      lastQuery: lastQuery.current,
      aiOverviewRequested: aiOverviewRequested.current
    });
    
    if (!query || lastQuery.current === query) {
      devLog('AI Overview useEffect: skipping - no query or same query');
      return;
    }
    
    devLog('AI Overview useEffect: calling fetchAIOverview');
    lastQuery.current = query;
    aiOverviewRequested.current = false;
    fetchAIOverview(query);
  }, [query, fetchAIOverview]);

  // Initialize Google CSE after preflight allows search
  useEffect(() => {
    const searchQuery = query?.trim();
    if (!searchQuery || blockedReason || !searchGateReady) {
      return;
    }

    const cseId = GOOGLE_CSE_IDS[currentSearchType] || GOOGLE_CSE_IDS.web;
    devLog('Debug CSE:', { currentSearchType, cseId, query: searchQuery });

    if (cseReadyRef.current && cseCxRef.current === cseId) {
      try {
        const element = window.google?.search?.cse?.element?.getElement(CSE_GNAME);
        if (element) {
          element.execute(searchQuery);
          setGcsLoaded(true);
          return;
        }
      } catch (error) {
        console.error('Error re-executing CSE search:', error);
      }
      cseReadyRef.current = false;
    }

    let cancelled = false;
    setGcsLoaded(false);
    cseReadyRef.current = false;

    const resultsRoot =
      searchResultsRef.current || document.getElementById(CSE_RESULTS_ROOT_ID);
    if (resultsRoot) {
      resultsRoot.innerHTML = '';
    }

    const renderAndExecute = () => {
      if (cancelled) return;
      const root = document.getElementById(CSE_RESULTS_ROOT_ID);
      if (!root) {
        console.error('CSE results root not found');
        return;
      }
      if (!root.querySelector('.gsc-control-cse')) {
        root.innerHTML = '';
      }
      try {
        if (!root.querySelector('.gsc-control-cse')) {
          window.google.search.cse.element.render({
            div: CSE_RESULTS_ROOT_ID,
            tag: 'searchresults-only',
            gname: CSE_GNAME,
            attributes: {
              linkTarget: '_blank',
              enableHistory: false,
              safeSearch: 'active',
            },
          });
        }
        const element = window.google.search.cse.element.getElement(CSE_GNAME);
        if (element) {
          element.execute(searchQuery);
          cseReadyRef.current = true;
          cseCxRef.current = cseId;
          setGcsLoaded(true);
          devLog('CSE search executed for:', searchQuery);
          setTimeout(() => window.dispatchEvent(new Event('resize')), 200);
        } else {
          console.error('CSE element not found after render');
        }
      } catch (error) {
        console.error('CSE render/execute failed:', error);
      }
    };

    const onCseReady = () => {
      devLog('CSE callback triggered');
      runWhenDocumentReady(renderAndExecute);
    };

    window.__gcse = {
      parsetags: 'explicit',
      callback: onCseReady,
    };

    if (window.google?.search?.cse?.element) {
      onCseReady();
    } else {
      const script = loadCseScript(cseId);
      if (script) {
        script.onerror = () => {
          if (cancelled) return;
          cseScriptCx = null;
          console.error('CSE script failed to load');
          toast.error('Failed to load search engine');
          const root = document.getElementById(CSE_RESULTS_ROOT_ID);
          if (root) {
            root.innerHTML =
              '<div style="color: var(--color-primary); padding: 32px; text-align: center; font-size: 20px;">Failed to load search results. Please try again later.</div>';
          }
        };
      }
    }

    return () => {
      cancelled = true;
    };
  }, [query, currentSearchType, blockedReason, searchGateReady]);

  useEffect(() => {
    return () => {
      aiOverviewRequested.current = false;
      cseReadyRef.current = false;
      cseCxRef.current = null;
    };
  }, [query]);

  // Shrink header on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (mainContainerRef.current) {
        if (mainContainerRef.current.scrollTop > 10) {
          setHeaderScrolled(true);
        } else {
          setHeaderScrolled(false);
        }
      }
    };
    
    const container = mainContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Function to scroll to latest message
  const scrollToLatestMessage = () => {
    setTimeout(() => {
      if (lastMessageRef.current) {
        lastMessageRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'end',
          inline: 'nearest'
        });
        // Adjust scroll position so the last message is not hidden by the chat input bar
        // Try to find the chat input bar (mobile or desktop)
        let chatInputBar = document.querySelector('.mobile-chat-input-box') || document.querySelector('.chat-input-box');
        let offset = 0;
        if (chatInputBar) {
          offset = chatInputBar.offsetHeight || 0;
        }
        // For main container scroll
        const mainContainer = document.querySelector('.search-main-container');
        if (mainContainer) {
          mainContainer.scrollBy({
            top: offset + 12, // add a little extra space
            behavior: 'smooth'
          });
        } else {
          // fallback to window scroll
          window.scrollBy({
            top: offset + 12,
            behavior: 'smooth'
          });
        }
      } else if (chatMessagesRef.current) {
        chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
      }
    }, 100);
  };

  // Function to scroll AI overview into view on mobile
  const scrollAIOverviewIntoView = () => {
    setTimeout(() => {
      const aiOverviewSection = document.querySelector('.ai-overview-chat-section');
      if (aiOverviewSection && window.innerWidth <= 768) {
        // Get the main container for better scroll positioning
        const mainContainer = document.querySelector('.search-main-container');
        if (mainContainer) {
          const containerRect = mainContainer.getBoundingClientRect();
          const sectionRect = aiOverviewSection.getBoundingClientRect();
          const scrollTop = mainContainer.scrollTop;
          const targetScrollTop = scrollTop + sectionRect.top - containerRect.top - 20; // 20px offset
          
          mainContainer.scrollTo({
            top: targetScrollTop,
            behavior: 'smooth'
          });
        } else {
          aiOverviewSection.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start',
            inline: 'nearest'
          });
        }
      }
    }, 200);
  };

      // Add useEffect to auto-scroll to bottom on chatMessages change
    useEffect(() => {
      if (!ENABLE_AI_SEARCH_OVERVIEW) return;
      // Check if the latest message is a typing message
      const latestMessage = chatMessages[chatMessages.length - 1];
      if (latestMessage && latestMessage.isTyping) {
        // For typing messages, let the SearchPage component handle scrolling
        // This prevents the initial scroll to AI overview
      } else {
        scrollToLatestMessage();
      }
    }, [chatMessages]);

  // Scroll to latest content when AI overview changes
  useEffect(() => {
    if (!ENABLE_AI_SEARCH_OVERVIEW) return;
    if (aiOverview || aiLoading) {
      // Only scroll on desktop to prevent interference with mobile chat scrolling
      if (window.innerWidth > 768) {
        scrollToLatestMessage();
      }
    }
  }, [aiOverview, aiLoading]);

  // Handle file upload
  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;
    
    const supportedFiles = files.filter(file => {
      const isSupported = supportedFileTypes[file.type] || 
                         (file.type.startsWith('image/') && file.type !== 'image/x-icon') ||
                         file.type.startsWith('text/');
      
      if (!isSupported) {
        toast.error(`${file.name} (${file.type}) is not a supported file type. Please upload images, text files, PDFs, or documents.`);
      }
      
      return isSupported;
    });
    
    if (supportedFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...supportedFiles]);
      
      supportedFiles.forEach(file => {
        trackChatEvent.fileUploaded(file.name);
      });
    }
    
    event.target.value = '';
  };

  // Remove file from selection
  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Upload files to S3
  const uploadFiles = async () => {
    const uploaded = [];
    for (const file of selectedFiles) {
      try {
        devLog('Starting file upload for:', file.name, file.type);
        
        const uploadData = await uploadAPI.getUploadUrl(file.type, getUserId());
        devLog('Upload API response:', uploadData);
        
        if (uploadData.error) {
          throw new Error(uploadData.error);
        }
        
        if (uploadData.statusCode === 400) {
          const errorBody = typeof uploadData.body === 'string' ? JSON.parse(uploadData.body) : uploadData.body;
          throw new Error(errorBody.error || 'Upload service error');
        }
        
        if (!uploadData || !uploadData.body) {
          throw new Error('Invalid upload data received from server');
        }
        
        const uploadDataBody = JSON.parse(uploadData.body);
        devLog('Parsed upload data:', uploadDataBody);
        
        if (!uploadDataBody.uploadUrl) {
          throw new Error('No upload URL received from server');
        }
        
        const uploadResult = await fetch(uploadDataBody.uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type }
        });
        
        if (!uploadResult.ok) {
          const errorText = await uploadResult.text();
          console.error('S3 upload failed:', uploadResult.status, errorText);
          throw new Error(`Failed to upload file to S3: ${uploadResult.status} ${errorText}`);
        }
        
        uploaded.push({
          name: file.name,
          type: file.type.startsWith('image/') ? 'image' : 'document',
          url: uploadDataBody.imageUrl
        });
      } catch (error) {
        console.error('File upload error:', error);
        toast.error(`Failed to upload ${file.name}: ${error.message}`);
      }
    }
    return uploaded;
  };

  // Learning assistant chat deprecated for now.
  const sendMessage = async (message) => {
    if (!message.trim()) return;
    toast.error('Learning assistant chat is temporarily unavailable.');
  };

  // Handle new chat
  const handleNewChat = () => {
    setChatMessages([]);
    setSelectedFiles([]);
    setShowAiOverview(true);
    setAiOverview(null);
    aiOverviewRequested.current = false;
    cseReadyRef.current = false;
    cseCxRef.current = null;
    navigate('/');
  };

  // Handle Enter key in message input
  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      const message = messageInputValue.trim();
      if (message) {
        trackChatEvent.messageSent('enter_key');
        sendMessage(message);
        setMessageInputValue('');
      }
    }
  };

  const handleNewSearch = () => {
    setShowAiOverview(true);
    setAiOverview(null);
    aiOverviewRequested.current = false;
    cseReadyRef.current = false;
    cseCxRef.current = null;
    navigate('/');
  };

  // Handler to enter chat focus mode
  const handleFocusChat = () => {
    setIsChatFocused(true);
    setAiFullScreen(true);
  };

  // Handler to exit chat focus mode
  const handleExitChat = () => setIsChatFocused(false);

  // Handler for mini-app focus
  const handleMiniAppFocus = () => {
    setAiFullScreen(true);
    setTimeout(() => {
      scrollToLatestMessage();
    }, 100);
  };

  // Handler to close app launcher when clicking outside
  const handleClickOutside = (event) => {
    if (showAppLauncher && !event.target.closest('.app-launcher-container')) {
      setShowAppLauncher(false);
    }
  };

  // Add click outside listener
  useEffect(() => {
    if (showAppLauncher) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showAppLauncher]);

  // Handler for submitting a new search from the top search box
  const handleTopSearch = async (e) => {
    e.preventDefault();
    const newQuery = searchInput.trim();
    if (!newQuery) return;
    
    trackUserAction.searchSubmitted(searchType || 'web');
    
    const user = getCurrentUser();
    const firebaseId = user ? user.uid : null;
    try {
      const preflight = await searchPreflight(newQuery);
      if (preflight.reason === 'auth_required') {
        toast.error('Sign in with your Kiddoware account to search.');
        navigate('/auth');
        return;
      }
      if (preflight.reason === 'license_required') {
        notifySearchLicenseRequired();
        return;
      }
      const failureKind = getPreflightFailureKind(preflight);
      if (failureKind === 'service_error') {
        toast.error(SEARCH_PREFLIGHT_SERVICE_ERROR_MESSAGE);
        setBlockedReason('service_error');
        setBlockedMessage(null);
        return;
      }
      if (failureKind === 'keyword_blocked') {
        setBlockedReason('keyword_blocked');
        setBlockedMessage(preflight.search_keyword);
        trackSafetyEvent.keywordBlocked(newQuery, firebaseId);
        return;
      }
      setBlockedMessage(null);
      setBlockedReason(null);
      window.location.href = `/s?q=${encodeURIComponent(newQuery)}&type=${currentSearchType}`;
    } catch (error) {
      console.error('Search preflight failed:', error);
      toast.error('Could not verify search access. Please try again.');
    }
  };

  // Launch app function
  const launchApp = (appConfig) => {
    trackMiniAppEvent.launched(appConfig.app);
    
    setChatMessages(prev => [...prev, { 
      id: Date.now(),
      type: 'assistant', 
      content: JSON.stringify(appConfig) 
    }]);
    setShowAppLauncher(false);
    toast.success('Mini-app launched!');
    
    setTimeout(() => {
      scrollToLatestMessage();
    }, 200);
  };

  // Handle suggestion button clicks
  const handleSuggestionClick = (category) => {
    setActiveCategory(category);
    setShowSuggestionBar(false);
    setShowSuggestionsList(true);
  };

  // Handle topic selection
  const handleTopicSelect = (topic) => {
    const message = prefixes[activeCategory] + topic;
    setMessageInputValue(message);
    if (messageInputRef.current) {
      messageInputRef.current.focus();
    }
    setShowSuggestionsList(false);
    setActiveCategory(null);
  };

  const aiOverviewVisible =
    ENABLE_AI_SEARCH_OVERVIEW &&
    ((currentSearchType === 'web' && (aiLoading || aiResponseType === 'direct_response' || chatMessages.length > 0)) ||
      (currentSearchType !== 'web' && (chatMessages.length > 0 || isChatFocused)));

  return {
    // State
    aiOverview,
    currentSearchType,
    aiLoading,
    aiResponseType,
    showAiOverview,
    gcsLoaded,
    chatMessages,
    selectedFiles,
    showSuggestionBar,
    showSuggestionsList,
    showAppLauncher,
    isChatFocused,
    searchInput,
    headerScrolled,
    blockedMessage,
    blockedReason,
    aiFullScreen,
    messageInputValue,
    activeCategory,
    profileOpen,
    aiOverviewVisible,
    
    // Refs
    searchResultsRef,
    searchBoxRef,
    messageInputRef,
    fileUploadRef,
    chatMessagesRef,
    lastMessageRef,
    mainContainerRef,
    profileRef,
    
    // Functions
    setCurrentSearchType,
    setShowAiOverview,
    setGcsLoaded,
    setChatMessages,
    setSelectedFiles,
    setShowSuggestionBar,
    setShowSuggestionsList,
    setShowAppLauncher,
    setIsChatFocused,
    setSearchInput,
    setHeaderScrolled,
    setBlockedMessage,
    setBlockedReason,
    setAiFullScreen,
    setMessageInputValue,
    setActiveCategory,
    setProfileOpen,
    
    handleFileUpload,
    removeFile,
    sendMessage,
    handleNewChat,
    handleKeyDown,
    handleNewSearch,
    handleFocusChat,
    handleExitChat,
    handleMiniAppFocus,
    handleTopSearch,
    launchApp,
    handleSuggestionClick,
    handleTopicSelect,
    scrollToLatestMessage,
    scrollAIOverviewIntoView,
    
    // Query
    query,
    navigate,
  };
}; 